import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ActionSheet } from "antd-mobile";
import { motion } from "framer-motion";
import { UserOutline, MailOutline, LockOutline, EyeInvisibleOutline, EyeOutline, GlobalOutline } from "antd-mobile-icons";
import {
  login,
  type LoginData,
  register,
  type RegisterData,
  resetPassword,
  type ResetPasswordData,
} from "@/lib/api/user";
import { setToken } from "@/lib/auth";
import { safeToast } from "@/lib/toast";
import { useI18n } from "@/hooks/useI18n";
import i18n from "@/i18n";
import { I18N_LANGUAGE_OPTIONS, type LanguageOption } from "@/constants/languages";

import Pattern from "@/components/Pattern";
import MentorAiSvgText from "@/components/Loaders/MentorAiSvgText";
import styled from "styled-components";
import logo from "@/assets/logo/heng.png";

interface LocationState {
  from?: {
    pathname: string;
  };
}

const StyledLoginWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  padding: 20px;
  position: relative;
  overflow-y: auto;

  .content-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: 550px;
    margin: 0 auto;
    min-height: 600px;
    padding: 20px 0;

    @media (max-width: 768px) {
      min-height: 550px;
      padding: 10px 0;
    }

    @media (max-height: 700px) {
      justify-content: flex-start;
      padding-top: 20px;
      padding-bottom: 20px;
    }

    @media (max-height: 600px) {
      min-height: auto;
      padding-top: 10px;
      padding-bottom: 10px;
    }
  }

  .logo-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 30px;
    opacity: 0.8;
    flex-shrink: 0;

    @media (max-width: 768px) {
      margin-bottom: 20px;
      transform: scale(0.9);
    }

    @media (max-height: 600px) {
      margin-bottom: 15px;
      transform: scale(0.8);
    }
  }

  .form-container {
    background-color: rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    transition: all 0.3s;
    width: 100%;
    max-width: 450px;
    padding: 30px;

    &:hover {
      box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
    }
  }

  .toggle-container {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
  }

  .toggle-buttons {
    display: flex;
    background-color: #f5f5f5;
    border-radius: 12px;
    padding: 4px;
  }

  .toggle-button {
    padding: 8px 24px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    border: none;
    background: none;
    color: #666;
    cursor: pointer;
    transition: all 0.3s;

    &.active {
      background-color: white;
      color: #333;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    }
  }

  .form {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  .flex-column {
    display: flex;
    flex-direction: column;

    label {
      color: #151717;
      font-weight: 600;
      margin-bottom: 5px;
    }
  }

  .inputForm {
    border: 1.5px solid #ecedec;
    border-radius: 10px;
    height: 50px;
    display: flex;
    align-items: center;
    padding-left: 10px;
    transition: 0.2s ease-in-out;
    background-color: white;
    margin-bottom: 15px;

    &:focus-within {
      border: 1.5px solid #2d79f3;
    }

    svg {
      margin-right: 10px;
      color: #666;
    }
  }

  .input {
    border: none;
    width: 85%;
    height: 100%;
    border-radius: 10px;

    &:focus {
      outline: none;
    }
  }

  .flex-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 15px;

    div {
      display: flex;
      align-items: center;

      input[type="checkbox"] {
        margin-right: 5px;
      }

      label {
        font-size: 14px;
        color: #333;
      }
    }
  }

  .span {
    font-size: 14px;
    color: #2d79f3;
    font-weight: 500;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .button-submit {
    background-color: #151717;
    border: none;
    color: white;
    font-size: 15px;
    font-weight: 500;
    border-radius: 10px;
    height: 50px;
    width: 100%;
    cursor: pointer;
    margin: 20px 0 10px 0;

    &:hover {
      background-color: #252727;
    }

    &:disabled {
      background-color: #666;
      cursor: not-allowed;
    }
  }

  .p {
    text-align: center;
    color: #333;
    font-size: 14px;
    margin: 5px 0;

    &.line {
      position: relative;
      margin: 15px 0;

      &:before,
      &:after {
        content: "";
        position: absolute;
        top: 50%;
        width: 45%;
        height: 1px;
        background-color: #ddd;
      }

      &:before {
        left: 0;
      }

      &:after {
        right: 0;
      }
    }
  }

  .social-buttons {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
  }

  .btn {
    width: 48%;
    height: 50px;
    border-radius: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: 500;
    gap: 10px;
    border: 1px solid #ededef;
    background-color: white;
    cursor: pointer;
    transition: 0.2s ease-in-out;

    &:hover {
      border: 1px solid #2d79f3;
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .footer {
    margin-top: 30px;
    text-align: center;
    color: rgba(0, 0, 0, 0.5);
    font-size: 14px;
  }

  /* 媒体查询适配PC端 */
  @media (min-width: 768px) {
    .form-container {
      padding: 40px;
      max-width: 500px;
    }

    .toggle-button {
      padding: 10px 30px;
      font-size: 16px;
    }

    .inputForm {
      height: 55px;
    }

    .button-submit {
      height: 55px;
      font-size: 16px;
    }

    .btn {
      height: 55px;
    }
  }

  @media (min-width: 1024px) {
    .form-container {
      max-width: 550px;
    }
  }
`;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const from = state?.from?.pathname || "/";
  const { t } = useI18n();

  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLanguageSheet, setShowLanguageSheet] = useState(false);

  // 使用统一的语言选项
  const languageOptions = I18N_LANGUAGE_OPTIONS.map(lang => ({
    text: lang.text,
    key: lang.value,
  }));

  const handleLanguageChange = async (language: string) => {
    try {
      await i18n.changeLanguage(language);
      setShowLanguageSheet(false);
      // 强制重新渲染页面以应用新语言
      window.location.reload();
    } catch (error) {
      console.error(t('auth.languageSwitchFailed'), error);
    }
  };
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
    confirmPassword: "",
    newPassword: "",
  });

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (isForgotPassword) {
        // 忘记密码功能
        if (!formData.username || !formData.email || !formData.newPassword) {
          safeToast.error(t('auth.fillAllFields'));
          setLoading(false);
          return;
        }
        const resetData: ResetPasswordData = {
          username: formData.username,
          email: formData.email,
          new_password: formData.newPassword,
        };
        const response = await resetPassword(resetData);
        if (response.success) {
          safeToast.success(t('auth.resetPasswordSuccess'));
          setIsForgotPassword(false);
          setIsLogin(true);
          setFormData({
            username: formData.username,
            password: "",
            email: "",
            confirmPassword: "",
            newPassword: "",
          });
        } else {
          safeToast.error(response.errorMessage || "密码重置失败");
        }
      } else if (isLogin) {
        const loginData: LoginData = {
          username: formData.username,
          password: formData.password,
        };
        const response = await login(loginData);
        if (response.success && response.data) {
          setToken(response.data.access_token);
          // 设置登录类型为普通用户登录
          localStorage.setItem('loginType', 'user');
          safeToast.success(t('auth.loginSuccess'));
          navigate(from, { replace: true });
        } else {
          safeToast.error(response.errorMessage || "登录失败");
        }
      } else {
        // Enhanced registration functionality
        if (formData.password !== formData.confirmPassword) {
          safeToast.error(t('auth.passwordMismatch'));
          setLoading(false);
          return;
        }
        const registerData: RegisterData = {
          username: formData.username,
          password: formData.password,
          email: formData.email,
        };
        const response = await register(registerData);
        if (response.success && response.data) {
          safeToast.success(t('auth.registerSuccess'));
          navigate(from, { replace: true });
        } else {
          safeToast.error(response.errorMessage || "注册失败");
        }
      }
    } catch (error: any) {
      safeToast.error(error.message || t('auth.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Pattern>
      <motion.div 
        className='absolute top-4 right-4 z-20 cursor-pointer'
        onClick={() => setShowLanguageSheet(true)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <GlobalOutline className="w-6 h-6 text-gray-600" />
      </motion.div>
      <StyledLoginWrapper>
        <div className='content-wrapper'>
          <div className='logo-container'>
              <img src={logo} alt="logo" className="h-16 w-auto" />
          </div>
        <div className='form-container'>
          <form
            className='form'
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            {isForgotPassword ? (
              <>
                <div className='flex-column'>
                  <label>{t('auth.username')}</label>
                </div>
                <div className='inputForm'>
                  <svg
                    height={20}
                    viewBox='0 0 32 32'
                    width={20}
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <g id='Layer_3' data-name='Layer 3'>
                      <path d='m30.853 13.87a15 15 0 0 0 -29.729 4.082 15.1 15.1 0 0 0 12.876 12.918 15.6 15.6 0 0 0 2.016.13 14.85 14.85 0 0 0 7.715-2.145 1 1 0 1 0 -1.031-1.711 13.007 13.007 0 1 1 5.458-6.529 2.149 2.149 0 0 1 -4.158-.759v-10.856a1 1 0 0 0 -2 0v1.726a8 8 0 1 0 .2 10.325 4.135 4.135 0 0 0 7.83.274 15.2 15.2 0 0 0 .823-7.455zm-14.853 8.13a6 6 0 1 1 6-6 6.006 6.006 0 0 1 -6 6z' />
                    </g>
                  </svg>
                  <input
                    type='text'
                    className='input'
                    placeholder={t('auth.enterUsername')}
                    name='username'
                    value={formData.username}
                    onChange={(e) =>
                      handleInputChange("username", e.target.value)
                    }
                  />
                </div>

                <div className='flex-column'>
                  <label>{t('auth.email')}</label>
                </div>
                <div className='inputForm'>
                  <svg
                    height={20}
                    width={20}
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 512 512'
                  >
                    <path d='M48 64C21.5 64 0 85.5 0 112c0 15.1 7.1 29.3 19.2 38.4L236.8 313.6c11.4 8.5 27 8.5 38.4 0L492.8 150.4c12.1-9.1 19.2-23.3 19.2-38.4c0-26.5-21.5-48-48-48H48zM0 176V384c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V176L294.4 339.2c-22.8 17.1-54 17.1-76.8 0L0 176z' />
                  </svg>
                  <input
                    type='email'
                    className='input'
                    placeholder={t('auth.enterEmail')}
                    name='email'
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                  />
                </div>

                <div className='flex-column'>
                  <label>{t('auth.newPassword')}</label>
                </div>
                <div className='inputForm'>
                  <svg
                    height={20}
                    viewBox='-64 0 512 512'
                    width={20}
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path d='m336 512h-288c-26.453125 0-48-21.523438-48-48v-224c0-26.476562 21.546875-48 48-48h288c26.453125 0 48 21.523438 48 48v224c0 26.476562-21.546875 48-48 48zm-288-288c-8.8125 0-16 7.167969-16 16v224c0 8.832031 7.1875 16 16 16h288c8.8125 0 16-7.167969 16-16v-224c0-8.832031-7.1875-16-16-16zm0 0' />
                    <path d='m304 224c-8.832031 0-16-7.167969-16-16v-80c0-52.929688-43.070312-96-96-96s-96 43.070312-96 96v80c0 8.832031-7.167969 16-16 16s-16-7.167969-16-16v-80c0-70.59375 57.40625-128 128-128s128 57.40625 128 128v80c0 8.832031-7.167969 16-16 16zm0 0' />
                  </svg>
                  <input
                    type='password'
                    className='input'
                    placeholder={t('auth.enterNewPassword')}
                    name='newPassword'
                    value={formData.newPassword}
                    onChange={(e) =>
                      handleInputChange("newPassword", e.target.value)
                    }
                  />
                </div>

                <button
                  className='button-submit'
                  type='submit'
                  disabled={loading}
                >
                  {loading ? t('auth.resetting') : t('auth.resetPassword')}
                </button>

                <p className='p'>
                  {t('auth.rememberedPassword')}{" "}
                  <span className='span' onClick={() => {
                    setIsForgotPassword(false);
                    setIsLogin(true);
                  }}>
                    {t('auth.backToLogin')}
                  </span>
                </p>
              </>
            ) : isLogin ? (
              <>
                <div className='flex-column'>
                  <label>{t('auth.username')}</label>
                </div>
                <div className='inputForm'>
                  <svg
                    height={20}
                    viewBox='0 0 32 32'
                    width={20}
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <g id='Layer_3' data-name='Layer 3'>
                      <path d='m30.853 13.87a15 15 0 0 0 -29.729 4.082 15.1 15.1 0 0 0 12.876 12.918 15.6 15.6 0 0 0 2.016.13 14.85 14.85 0 0 0 7.715-2.145 1 1 0 1 0 -1.031-1.711 13.007 13.007 0 1 1 5.458-6.529 2.149 2.149 0 0 1 -4.158-.759v-10.856a1 1 0 0 0 -2 0v1.726a8 8 0 1 0 .2 10.325 4.135 4.135 0 0 0 7.83.274 15.2 15.2 0 0 0 .823-7.455zm-14.853 8.13a6 6 0 1 1 6-6 6.006 6.006 0 0 1 -6 6z' />
                    </g>
                  </svg>
                  <input
                    type='text'
                    className='input'
                    placeholder={t('auth.enterUsername')}
                    name='username'
                    value={formData.username}
                    onChange={(e) =>
                      handleInputChange("username", e.target.value)
                    }
                  />
                </div>

                <div className='flex-column'>
                  <label>{t('auth.password')}</label>
                </div>
                <div className='inputForm'>
                  <svg
                    height={20}
                    viewBox='-64 0 512 512'
                    width={20}
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path d='m336 512h-288c-26.453125 0-48-21.523438-48-48v-224c0-26.476562 21.546875-48 48-48h288c26.453125 0 48 21.523438 48 48v224c0 26.476562-21.546875 48-48 48zm-288-288c-8.8125 0-16 7.167969-16 16v224c0 8.832031 7.1875 16 16 16h288c8.8125 0 16-7.167969 16-16v-224c0-8.832031-7.1875-16-16-16zm0 0' />
                    <path d='m304 224c-8.832031 0-16-7.167969-16-16v-80c0-52.929688-43.070312-96-96-96s-96 43.070312-96 96v80c0 8.832031-7.167969 16-16 16s-16-7.167969-16-16v-80c0-70.59375 57.40625-128 128-128s128 57.40625 128 128v80c0 8.832031-7.167969 16-16 16zm0 0' />
                  </svg>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className='input'
                    placeholder={t('auth.enterPassword')}
                    name='password'
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                  />
                  <div 
                    className="cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOutline className="text-gray-500" />
                    ) : (
                      <EyeInvisibleOutline className="text-gray-500" />
                    )}
                  </div>
                </div>

                <div className='flex-row'>
                  <div>
                    <input type='checkbox' id='remember' />
                    <label htmlFor='remember'>{t('auth.rememberMe')}</label>
                  </div>
                  <span className='span' onClick={() => {
                    setIsForgotPassword(true);
                    setIsLogin(false);
                  }}>{t('auth.forgotPassword')}</span>
                </div>

                <button
                  className='button-submit'
                  type='submit'
                  disabled={loading}
                >
                  {loading ? t('auth.loggingIn') : t('auth.login')}
                </button>

                <p className='p'>
                  {t('auth.noAccount')}{" "}
                  <span className='span' onClick={() => setIsLogin(false)}>
                    {t('auth.register')}
                  </span>
                </p>
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex flex-col mb-4">
                  <label className="text-gray-800 font-semibold mb-2">{t('auth.username')}</label>
                  <div className="border-2 border-gray-200 rounded-xl h-12 flex items-center px-3 transition-all duration-200 bg-white focus-within:border-blue-500">
                    <UserOutline className="text-gray-500 mr-3" />
                    <input
                      type="text"
                      className="border-none w-full h-full rounded-xl focus:outline-none"
                      placeholder={t('auth.enterUsername')}
                      name="username"
                      value={formData.username}
                      onChange={(e) => handleInputChange("username", e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex flex-col mb-4">
                  <label className="text-gray-800 font-semibold mb-2">{t('auth.email')}</label>
                  <div className="border-2 border-gray-200 rounded-xl h-12 flex items-center px-3 transition-all duration-200 bg-white focus-within:border-blue-500">
                    <MailOutline className="text-gray-500 mr-3" />
                    <input
                      type="email"
                      className="border-none w-full h-full rounded-xl focus:outline-none"
                      placeholder={t('auth.enterEmail')}
                      name="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex flex-col mb-4">
                  <label className="text-gray-800 font-semibold mb-2">{t('auth.password')}</label>
                  <div className="border-2 border-gray-200 rounded-xl h-12 flex items-center px-3 transition-all duration-200 bg-white focus-within:border-blue-500">
                    <LockOutline className="text-gray-500 mr-3" />
                    <input
                      type={showPassword ? "text" : "password"}
                      className="border-none flex-1 h-full rounded-xl focus:outline-none"
                      placeholder={t('auth.enterPassword')}
                      name="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange("password", e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {showPassword ? <EyeInvisibleOutline /> : <EyeOutline />}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col mb-4">
                  <label className="text-gray-800 font-semibold mb-2">{t('auth.confirmPassword')}</label>
                  <div className="border-2 border-gray-200 rounded-xl h-12 flex items-center px-3 transition-all duration-200 bg-white focus-within:border-blue-500">
                    <LockOutline className="text-gray-500 mr-3" />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      className="border-none flex-1 h-full rounded-xl focus:outline-none"
                      placeholder={t('auth.enterPasswordAgain')}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {showConfirmPassword ? <EyeInvisibleOutline /> : <EyeOutline />}
                    </button>
                  </div>
                </div>

                <motion.button
                  className="bg-gray-800 hover:bg-gray-900 disabled:bg-gray-400 text-white font-medium rounded-xl h-12 w-full cursor-pointer my-5 transition-colors duration-200"
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? t('auth.registering') : t('auth.register')}
                </motion.button>

                <p className="text-center text-gray-600 text-sm mt-2">
                  {t('auth.haveAccount')}{" "}
                  <span 
                    className="text-blue-500 font-medium cursor-pointer hover:underline"
                    onClick={() => setIsLogin(true)}
                  >
                    {t('auth.login')}
                  </span>
                </p>
              </motion.div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className='footer'>
          <p>© 2025 ZEVOLVE AI</p>
        </div>
        </div>
      </StyledLoginWrapper>

      <ActionSheet
          visible={showLanguageSheet}
          actions={languageOptions}
          onClose={() => setShowLanguageSheet(false)}
          onAction={(action, index) => {
            if (typeof action === 'object' && action !== null && 'key' in action) {
              handleLanguageChange(String(action.key));
            }
          }}
          cancelText={t('common.cancel')}
        />
    </Pattern>
  );
}
